import { useApolloClient, useMutation } from '@apollo/client'
import { toast } from 'sonner'

import { DISPATCHER_FRAGMENT, SHIPMENT_FRAGMENT } from '../../../../../../data/fragments'
import { ASSIGN_DISPATCHER, UNASSIGN_DISPATCHER } from '../../../../../../data/mutations/member'
import { UPDATE_SHIPMENT_STATUS } from '../../../../../../data/mutations/shipment'
import { useUserQuery } from '../../../../../../hooks/use-user-query'
import type { TShipmentMember } from '../../../../../../types/shipment'
import { ShipmentStatusEnum } from '../../../../../../types/shipment'
import { MembershipAccessType } from '../../../../../../types/user'
import { removeArrayItemFromCache, updateCacheFieldWithNewData } from '../../../../../../utils/graphql-cache'
import type { IAssignDispatcherInput } from '../../types'
import { useShipmentMutations } from './use-shipment-mutations'
/**
 * This custom hook is used to handle the mutations for adding and removing dispatchers from a shipment.
 * It also updates the cache with the new data after adding or removing a dispatcher.
 */
export const useDispatcherMutations = (
  dispatchers: TShipmentMember[],
  onClose: () => void,
  shipmentID: string,
  shipmentNumber: string,
  shipmentStatus: ShipmentStatusEnum,
  workspaceID: string,
  onError?: () => void
) => {
  const client = useApolloClient()
  const { myDetails } = useUserQuery()

  const customHeaderContext = {
    headers: workspaceID ? { 'ws-id': workspaceID } : {}
  }

  const { handleInviteToWorkspace } = useShipmentMutations({ workspaceID })

  const [assignDispatcher] = useMutation(ASSIGN_DISPATCHER, {
    context: customHeaderContext,
    onCompleted: (data) => {
      toast.success('Dispatcher added successfully')
      onClose()
      const { email, phone, id } = data?.assignDispatcher.user || {}

      const isAddedByADispatcher = dispatchers.some(({ user }) => user.id === myDetails?.id)
      if (isAddedByADispatcher) {
        handleInviteToWorkspace({ accessType: MembershipAccessType.MEMBER, emailOrPhone: email || phone, id })
      }
    },
    onError: (err) => {
      if (err.message.includes('The conditional request failed')) {
        toast.error('Dispatcher already exists')
        onError?.()
      } else {
        toast.error(`Failed to add dispatcher: ${err.message}`)
        onError?.()
      }
      onClose()
    },
    update: updateCacheFieldWithNewData({
      cacheFieldKey: 'dispatchers',
      entityID: shipmentID,
      entityTypeName: 'Shipment',
      fragment: DISPATCHER_FRAGMENT,
      mutationResponseKey: 'assignDispatcher'
    })
  })

  // Mutation hook for updating the shipment status
  const [updateShipmentStatus] = useMutation(UPDATE_SHIPMENT_STATUS)

  const [unassignDispatcher, { loading: removingDispatcherFromShipment }] = useMutation(UNASSIGN_DISPATCHER, {
    context: customHeaderContext,
    onCompleted: () => {
      toast.success('Dispatcher removed successfully')
      const updatedShipment = client.readFragment({
        fragment: SHIPMENT_FRAGMENT,
        id: `Shipment:${shipmentID}`
      })

      if (updatedShipment) {
        const { dispatchers, drivers } = updatedShipment
        if (dispatchers.length === 0 && drivers.length === 0 && shipmentStatus === ShipmentStatusEnum.CarrierAssigned) {
          updateShipmentStatus({
            variables: {
              id: shipmentID,
              shipmentStatus: ShipmentStatusEnum.NeedsCarrier
            }
          })
        }
      }
    },
    update: removeArrayItemFromCache({
      fieldName: 'dispatchers',
      id: shipmentID,
      mutationReturnValueFieldName: 'unassignDispatcher',
      typename: 'Shipment'
    })
  })

  const handleAddDispatcher = async (input: IAssignDispatcherInput) => {
    await assignDispatcher({
      variables: { ...input, shipmentID, shipmentNumber }
    })
  }

  const handleRemoveDispatcher = async ({
    userID,
    dispatcherWorkspaceID
  }: {
    dispatcherWorkspaceID: string
    userID: string
  }) => {
    await unassignDispatcher({
      variables: {
        dispatcherID: userID,
        dispatcherWorkspaceID: dispatcherWorkspaceID,
        removedByWorkspaceID: workspaceID,
        shipmentID: shipmentID
      }
    })
  }

  return {
    handleAddDispatcher,
    handleRemoveDispatcher,
    isLoading: removingDispatcherFromShipment
  }
}
